import Drawable from '../entities/Drawable.js';
import { imageStore, spriteStore } from '../images.js';
import Sprite from './Sprite.js';
import TypeBadge from './TypeBadge.js';
import PixelText from './PixelText.js';

import { getOSM } from '../OverworldAnimation.js';
import { c } from '../setup.js';

class PokedexLineItemText extends PixelText {
    constructor({ id, name = '--', text = '', position = { x: 161, y: 83 }, color = 'black', pokedexMenu }) {
        super({ text, position, color });
        this.id = id;
        this.name = name;
        this.text = `${id}`.padStart(3, '0') + name;
        this.pokedexMenu = pokedexMenu;
        this.position = {
            x: 161,
            y: 83 - 7 + 16 * id - pokedexMenu.currentOption * 16,
        };
    }

    update() {
        this.name = this.pokedexMenu.seenAndCaughtPokemon[this.id]?.name ?? '--';
        this.position.y = 83 - 7 + 16 * (this.id - 1) - this.pokedexMenu.currentOption * 16;
        this.text = `${`${this.id}`.padStart(3, '0')} ${this.name}`;
    }
}

class PokedexLineItem extends Drawable {
    constructor({ id, lineItemText, position = { x: 160, y: 83 }, pokedexMenu }) {
        super({ position });
        this.pokedexMenu = pokedexMenu;
        this.id = id;
        this.lineItemText = lineItemText;
        this.position = position;
        this.caughtIcon = new Sprite({
            image: imageStore.get('img/caught_pokemon_pokedex_icon.png'),
            visible: false,
            position: {
                x: position.x - 8,
                y: position.y,
            },
        });
    }

    update() {
        this.position.y = 83 + 16 * (this.id - 1) - this.pokedexMenu.currentOption * 16;
        this.caughtIcon.position.y = this.position.y - 7;
        this.lineItemText.name = this.lineItemText.update();
    }

    draw() {
        this.position.y = 83 - 7 + 16 * (this.id - 1) - this.pokedexMenu.currentOption * 16;
        this.lineItemText.position.y = this.position.y;
        this.caughtIcon.position.y = this.position.y;

        this.lineItemText.draw();

        // Check if pokemon is caught using regional dex order
        const pokemonId = this.pokedexMenu.orderToPokemonIdMap[this.id];
        const region = this.pokedexMenu.pokedex?.regions?.[this.pokedexMenu.currentRegion];
        // Check regional dex first, then fall back to top-level (same as refreshPokedex)
        const caughtPokemon = pokemonId && (region?.caught?.[pokemonId] || this.pokedexMenu.pokedex?.caught?.[pokemonId]);
        this.caughtIcon.visible = !!caughtPokemon;
        this.caughtIcon.draw();
    }
}

export default class PokedexMenu extends Drawable {
    constructor() {
        super();
        this.pokedex = getOSM().player.pokedex;
        this.visible = false;
        this.currentOption = 0;
        this.seenAndCaughtPokemon = {};
        this.currentRegion = 'boriken';
        this.regionalEntries = []; // Array of entries sorted by order
        this.orderToPokemonIdMap = {}; // Maps regional order to pokemon ID

        // Load regional dex entries
        this.loadRegionalEntries();

        // Create sprite with spritesheet parameters
        this.currentPokemonSprite = new Sprite({
            position: {
                x: 74,
                y: 48,
            },
            image: imageStore.get('img/spritesheets/pokemon_front_master_spritesheet_20_20.png'),
            isFromSpriteSheet: true,
            spriteSheetCols: 20,
            spriteSheetRows: 20,
            spriteIndex: 0, // Will be updated in draw()
        });

        this.sprites = {
            background: spriteStore.store.pokedexMenuBackgroundSprite,
            currentPokemon: null,
        };

        this.pokemonList = [];
        // Create list items based on regional dex entries length
        for (let i = 0; i < this.regionalEntries.length; i++) {
            const lineItemText = new PokedexLineItemText({ id: i + 1, pokedexMenu: this });
            const lineItem = new PokedexLineItem({
                id: i + 1,
                pokedexMenu: this,
                lineItemText: lineItemText,
            });
            this.pokemonList.push(lineItem);
            this.seenAndCaughtPokemon[i + 1] = null;
        }

        // Initial refresh to populate pokemon data
        this.refreshPokedex();

        this.currentPokemonNameText = new PixelText({
            text: '--',
            position: {
                x: 7,
                y: 28 - 7,
            },
        });

        this.currentPokemonClassificationText = new PixelText({
            text: '--',
            position: {
                x: 7,
                y: 39 - 7,
            },
        });

        this.typeBadge1 = new TypeBadge({
            type: null,
            position: { x: 71, y: 17 },
            visible: true,
        });
        this.typeBadge2 = new TypeBadge({
            type: null,
            position: { x: 71 + 34, y: 17 },
            visible: true,
        });

        this.pokedexEntryText = new PixelText({
            text: 'Hello world! This is a super long text. Hopeflly it wraps around! hahahahah that would be great!',
            position: {
                x: 7,
                y: 128 - 7,
            },
            maxWidth: 132,
            lineHeight: 0,
        });

        this.numCaughtText = new PixelText({
            text: '99',
            position: {
                x: 104,
                y: 11 - 7,
            },
        });

        this.numSeenText = new PixelText({
            text: '99',
            position: {
                x: 124,
                y: 11 - 7,
            },
        });
    }

    loadRegionalEntries() {
        try {
            const pokedexDataJson = localStorage.getItem('pokedex_data');
            if (pokedexDataJson) {
                const pokedexData = JSON.parse(pokedexDataJson);
                const regionData = pokedexData[this.currentRegion];

                if (regionData && Array.isArray(regionData)) {
                    // Sort entries by order
                    this.regionalEntries = [...regionData].sort((a, b) => a.order - b.order);

                    // Create mapping from regional order to pokemon ID
                    this.regionalEntries.forEach((entry) => {
                        const pokemonId = entry.pokemon?.id || entry.pokemonId || entry.id;
                        if (pokemonId) {
                            this.orderToPokemonIdMap[entry.order] = pokemonId;
                        }
                    });
                }
            }
        } catch (error) {
            console.error(`Error loading regional entries: ${error.message}`);
        }
    }

    draw() {
        if (this.pokedex !== getOSM().player.pokedex) {
            this.pokedex = getOSM().player.pokedex;
            // Refresh pokedex data when pokedex changes
            this.refreshPokedex();
        }
        if (!this.visible) {
            return;
        }

        this.sprites.background.draw();
        const currentPokemon = this.seenAndCaughtPokemon[this.currentOption + 1];

        // Calculate seen/caught counts for current region
        const region = this.pokedex.regions[this.currentRegion];
        const seenCount = Object.values(region?.seen || {}).filter((value) => value !== null).length;
        const caughtCount = Object.values(region?.caught || {}).filter((value) => value !== null).length;
        this.numCaughtText.text = caughtCount.toString().padStart(3, '0');
        this.numCaughtText.draw();
        this.numSeenText.text = seenCount.toString().padStart(3, '0');
        this.numSeenText.draw();

        if (currentPokemon) {
            if (currentPokemon.type1 === currentPokemon.type2) {
                this.typeBadge1.position = { x: 71 + 34, y: 17 };
            } else {
                this.typeBadge1.position = { x: 71, y: 17 };
                this.typeBadge2.position = { x: 71 + 34, y: 17 };
            }
            this.typeBadge1.type = currentPokemon.type1;
            this.typeBadge2.type = currentPokemon.type2;

            this.typeBadge1.draw();
            this.typeBadge2.draw();
        }

        if (currentPokemon !== undefined) {
            if (currentPokemon !== null) {
                // Update sprite index based on Pokemon ID
                this.currentPokemonSprite.spriteIndex = currentPokemon.id - 1;
                this.sprites.currentPokemon = this.currentPokemonSprite;
            } else {
                this.sprites.currentPokemon = spriteStore.store.missingNoSprite;
            }
        } else {
            this.sprites.currentPokemon = spriteStore.store.missingNoSprite;
        }

        this.pokedexEntryText.draw();

        this.pokemonList.forEach((listItem) => {
            listItem.update();
            if (listItem.position.y < 180 && listItem.position.y > -10) {
                listItem.draw();
            }
        });

        this.sprites.currentPokemon.position = {
            x: 74,
            y: 48,
        };
        this.sprites.currentPokemon.draw();
        this.currentPokemonNameText.text = currentPokemon?.name ?? '--';
        this.currentPokemonNameText.draw();
        this.currentPokemonClassificationText.text = currentPokemon?.classification ?? '--';
        this.currentPokemonClassificationText.draw();
        this.pokedexEntryText.text = currentPokemon?.pokedexEntry ?? '--';

        // Get current pokemon's base stats
        const currentPokemonBaseStats = this.seenAndCaughtPokemon[this.currentOption + 1]?.baseStats ?? {
            maxHp: 0,
            attack: 0,
            defense: 0,
            specialAttack: 0,
            specialDefense: 0,
            speed: 0,
        };

        const stats = [
            currentPokemonBaseStats.maxHp,
            currentPokemonBaseStats.attack,
            currentPokemonBaseStats.specialAttack,
            currentPokemonBaseStats.speed,
            currentPokemonBaseStats.specialDefense,
            currentPokemonBaseStats.defense,
        ];

        c.fillStyle = 'rgba(0, 180, 0, 0.5)';

        const centerX = 35;
        const centerY = 80;
        const radius = 29;

        c.beginPath();
        for (let i = 0; i < 6; i++) {
            const angle = (Math.PI * 2 * i) / 6 - Math.PI / 2;
            const statValue = stats[i] * 0.008;
            const pointRadius = Math.max(5, Math.min(radius * statValue, 32));
            const x = Math.round(centerX + pointRadius * Math.cos(angle));
            const y = Math.round(centerY + pointRadius * Math.sin(angle));

            if (i === 0) {
                c.moveTo(x, y);
            } else {
                c.lineTo(x, y);
            }
        }
        c.closePath();
        c.fill();
    }

    getPartyPokemon() {
        const player = getOSM().player;
        if (!player || !player.party) {
            return [];
        }
        // Filter out null entries and return array of Pokemon objects
        return player.party.filter((pokemon) => pokemon !== null && pokemon !== undefined);
    }

    getPCPokemon() {
        const player = getOSM().player;
        if (!player || !player.pcPokemon) {
            return [];
        }
        // Flatten the 3D array structure [box][row][column] into a single array
        const allPCPokemon = [];
        for (const box of player.pcPokemon) {
            if (box && Array.isArray(box)) {
                for (const row of box) {
                    if (row && Array.isArray(row)) {
                        for (const pokemon of row) {
                            if (pokemon !== null && pokemon !== undefined) {
                                allPCPokemon.push(pokemon);
                            }
                        }
                    }
                }
            }
        }
        return allPCPokemon;
    }

    refreshPokedex() {
        const region = this.pokedex.regions[this.currentRegion];

        // Iterate over the player's party pokemon as well as pokemon in the PC,
        // Add all pokemon to Seen and Caught if they haven't been added already
        const partyPokemon = this.getPartyPokemon();
        const pcPokemon = this.getPCPokemon();

        // Add party pokemon to caught and seen
        partyPokemon.forEach((pokemon) => {
            if (pokemon && pokemon.id) {
                this.pokedex.addCaughtPokemon(pokemon);
                this.pokedex.addSeenPokemon(pokemon);
            }
        });

        // Add PC pokemon to caught and seen
        pcPokemon.forEach((pokemon) => {
            if (pokemon && pokemon.id) {
                this.pokedex.addCaughtPokemon(pokemon);
                this.pokedex.addSeenPokemon(pokemon);
            }
        });

        this.pokemonList.forEach((listItem, i) => {
            const regionalOrder = i + 1;
            const pokemonId = this.orderToPokemonIdMap[regionalOrder];

            if (pokemonId) {
                // Check regional dex first, then fall back to top-level
                const seenPokemon = region?.seen?.[pokemonId] || this.pokedex.seen[pokemonId];
                const caughtPokemon = region?.caught?.[pokemonId] || this.pokedex.caught[pokemonId];

                if (caughtPokemon) {
                    listItem.name = caughtPokemon.name;
                    this.seenAndCaughtPokemon[regionalOrder] = caughtPokemon;
                } else if (seenPokemon) {
                    listItem.name = seenPokemon.name;
                    this.seenAndCaughtPokemon[regionalOrder] = seenPokemon;
                } else {
                    this.seenAndCaughtPokemon[regionalOrder] = null;
                }
            }
        });
    }

    getMaxPokemonCount() {
        // Return the number of pokemon in the current regional dex
        return this.regionalEntries.length;
    }
}
